// ============================================================================
// GumaHUB Shared Types
// ============================================================================

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Authentication Types
export interface ModuleAccess {
  access: boolean;
  roles: string[];
  permissions: string[];
  isAdmin: boolean;
}

// Company context for multi-company support
export interface UserCompany {
  clientId: string;
  clientName: string;
  clientCode: string;
  isDefault: boolean;
}

export interface AuthUser {
  userId: string;
  username: string;
  displayName: string;
  // Current active company (backward compatible)
  clientId: string;
  clientName: string;
  // Employee ID for current company (null if user has no employee record)
  employeeId: string | null;
  // Multi-company support
  availableCompanies: UserCompany[];
  modules: Record<string, ModuleAccess>;
  isPlatformAdmin: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  user: AuthUser;
}

// Module Types
export type ModuleId = 'ENT' | 'ORD' | 'SHP' | 'BID' | 'EMP' | 'EXP' | 'TRV' | 'RPT' | 'SRV' | 'TIME' | 'DOC' | 'SVC';

export interface Module {
  moduleId: ModuleId;
  moduleName: string;
  description: string;
  icon: string;
  route: string;
  isCore: boolean;
}

export const MODULES: Record<ModuleId, Module> = {
  ENT: {
    moduleId: 'ENT',
    moduleName: 'Enterprise',
    description: 'Client and supplier/customer management',
    icon: 'Building2',
    route: '/enterprise',
    isCore: true,
  },
  ORD: {
    moduleId: 'ORD',
    moduleName: 'Order Management',
    description: 'Purchase and sales order management',
    icon: 'ShoppingCart',
    route: '/orders',
    isCore: false,
  },
  SHP: {
    moduleId: 'SHP',
    moduleName: 'Shipment',
    description: 'Shipment tracking and management',
    icon: 'Truck',
    route: '/shipments',
    isCore: false,
  },
  BID: {
    moduleId: 'BID',
    moduleName: 'Bid Management',
    description: 'RFQ and bidding process management',
    icon: 'Gavel',
    route: '/bids',
    isCore: false,
  },
  EMP: {
    moduleId: 'EMP',
    moduleName: 'Employee Organization',
    description: 'Employee and organization management',
    icon: 'Users',
    route: '/employees',
    isCore: false,
  },
  EXP: {
    moduleId: 'EXP',
    moduleName: 'Expense',
    description: 'Expense tracking and approval',
    icon: 'Receipt',
    route: '/expenses',
    isCore: false,
  },
  TRV: {
    moduleId: 'TRV',
    moduleName: 'Travel',
    description: 'Travel request and booking management',
    icon: 'Plane',
    route: '/travel',
    isCore: false,
  },
  RPT: {
    moduleId: 'RPT',
    moduleName: 'Reports',
    description: 'Report management and analytics',
    icon: 'ChartBar',
    route: '/reports',
    isCore: false,
  },
  TIME: {
    moduleId: 'TIME',
    moduleName: 'Time & Attendance',
    description: 'Work schedules, clock in/out, timesheets and overtime',
    icon: 'Clock',
    route: '/time',
    isCore: false,
  },
  SRV: {
    moduleId: 'SRV',
    moduleName: 'Survey',
    description: 'Survey & feedback — engagement, pulse, 360°, exit, onboarding',
    icon: 'ClipboardList',
    route: '/surveys',
    isCore: false,
  },
  DOC: {
    moduleId: 'DOC',
    moduleName: 'Document Management',
    description: 'Shared document library — upload, organize and share files with partner companies',
    icon: 'Folder',
    route: '/documents',
    isCore: false,
  },
  SVC: {
    moduleId: 'SVC',
    moduleName: 'Service',
    description: 'Field service / service desk — requests, work orders, catalogs',
    icon: 'Wrench',
    route: '/service',
    isCore: false,
  },
};

// Enterprise Types
export interface Client {
  clientId: string;
  clientCode: string;
  clientName: string;
  legalName?: string;
  taxId?: string;
  defaultCurrency: string;
  defaultCountry: string;
  isActive: boolean;
}

export interface SupplierCustomerRelation {
  relId: string;
  supplierId: string;
  customerId: string;
  status: 'active' | 'pending' | 'suspended' | 'terminated';
  relationshipType: 'direct' | 'distributor' | 'broker' | 'marketplace';
  supplierCode?: string;
  customerCode?: string;
  paymentTerms?: string;
  creditLimit?: number;
  currencyCode: string;
}

// Reference Data Types
export interface Currency {
  currencyCode: string;
  currencyName: string;
  symbol: string;
}

export interface Country {
  countryCode: string;
  countryName: string;
  currencyCode?: string;
}

export interface City {
  cityId: string;
  cityName: string;
  countryCode: string;
  stateProvince?: string;
}

export interface Incoterm {
  incotermCode: string;
  incotermName: string;
  description?: string;
}

export interface ShipVia {
  shipViaCode: string;
  shipViaName: string;
  carrierType: string;
}

// Order Types
export type OrderType = 'PO' | 'SO';
export type OrderStatus = 'draft' | 'submitted' | 'confirmed' | 'in_progress' | 'shipped' | 'delivered' | 'cancelled' | 'closed';

export interface OrderHeader {
  orderId: string;
  orderNumber: string;
  orderType: OrderType;
  clientId: string;
  tradingPartnerId: string;
  status: OrderStatus;
  orderDate: string;
  requiredDate?: string;
  promisedDate?: string;
  currencyCode: string;
  totalAmount: number;
  notes?: string;
}

export interface OrderLine {
  lineId: string;
  orderId: string;
  lineNumber: number;
  itemCode: string;
  itemDescription: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  unitOfMeasure: string;
}

// Shipment Types
export type ShipmentStatus = 'planned' | 'booked' | 'in_transit' | 'at_port' | 'customs' | 'delivered' | 'cancelled';
export type TransportMode = 'sea' | 'air' | 'road' | 'rail' | 'multimodal';

export interface Shipment {
  shipmentId: string;
  shipmentNumber: string;
  clientId: string;
  tradingPartnerId?: string;
  status: ShipmentStatus;
  transportMode: TransportMode;
  originCountry: string;
  originCity?: string;
  destinationCountry: string;
  destinationCity?: string;
  etd?: string;
  eta?: string;
  atd?: string;
  ata?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  masterBol?: string;
  houseBol?: string;
}

// Form Types
// Note: SelectOption is defined in components/Select.tsx with additional 'disabled' field

export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
}

// Utility Types
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface FilterConfig {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'startsWith' | 'endsWith';
  value: string | number | boolean;
}
